import { TFunction } from "i18next";
import { getClientLogoByClientId } from "../services/api/client.api";
import { logErrorAndNotifyUserForRoosterInitiatedLinks } from "../services/api/errorLoggerAndNotifier";
import { getRoosterAutobookAvailabilityData } from "../services/api/meeting.api";
import {
  SchedulerStoreType,
  useSchedulerStore
} from "../state/scheduler.store";
import {
  handleRoosterAutobookAvailabilityValidationErrorMessageState,
  setInitializationFailedState,
  setLinkNotFoundState
} from "../utils/errorManagement";
import { verifyInternalRefToken } from "../services/api/meeting.api";
import { getLinkType, SourceTypeEnum } from "./link";
import { handleAutobookLink } from "./meeting/autobookLink";
import { handleAvailabilityLink } from "./meeting/availabilityLink";
import { handleRoosterLink } from "./meeting/roosterLink";

export const initializeSchedulerStore = async (
  actions: SchedulerStoreType["actions"],
  t: TFunction,
  requestSessionStartedAt = Date.now()
) => {
  try {
    await runInitialization(actions, t, requestSessionStartedAt);
  } finally {
    // R495-619: whatever happened above — an early return, an unhandled throw,
    // or a link shape we do not recognise — the candidate must not be left on
    // an endless loading skeleton. If nothing moved the step off "loading",
    // say so and offer a retry. Read through the store rather than a captured
    // value so a step set deep inside a handler is seen.
    if (useSchedulerStore.getState().ui?.currentStep === "loading") {
      setInitializationFailedState(actions, t);
    }
  }
};

const runInitialization = async (
  actions: SchedulerStoreType["actions"],
  t: TFunction,
  requestSessionStartedAt: number
) => {
  const linkType = getLinkType();

  actions.setLink(
    null,
    null,
    linkType?.availabilityId ?? "",
    linkType?.viewMode ?? null
  );

  actions.setReferer(linkType?.referer);

  if (linkType?.internalRef) {
    const verifiedClient = await verifyInternalRefToken(linkType.internalRef);
    actions.setVerifiedInternalClient(verifiedClient);
  }

  // handle rooster link
  if (linkType?.type === SourceTypeEnum.roosterlink && linkType.userId) {
    await handleRoosterLink(actions, linkType, t, requestSessionStartedAt);
  }

  // handle rooster autobook or availability link
  if (linkType?.type === SourceTypeEnum.rooster && linkType?.linkId) {
    actions.setLink("", null, linkType.linkId, null);
    const autobookAvailabilityData = await getRoosterAutobookAvailabilityData({
      linkId: linkType?.linkId
    });

    if (autobookAvailabilityData && "error" in autobookAvailabilityData) {
      handleRoosterAutobookAvailabilityValidationErrorMessageState(
        actions,
        {
          code: autobookAvailabilityData.error,
          initiatorName: autobookAvailabilityData?.initiatorName,
          initiatorEmail: autobookAvailabilityData?.initiatorEmail
        },
        t
      );
      await logErrorAndNotifyUserForRoosterInitiatedLinks({
        errorCode: autobookAvailabilityData.error,
        context: { linkId: linkType?.linkId }
      });
      return;
    }

    if (!autobookAvailabilityData) {
      setLinkNotFoundState(
        actions,
        t("ROOSTER.PUBLIC-APP.LINK_NOT_FOUND", {
          defaultValue: "Link is not found"
        }),
        t("ROOSTER.PUBLIC-APP.INTERVIEW_NOT_FOUND", {
          defaultValue:
            "This interview could not be found. Please contact your recruiter."
        })
      );
      return;
    }

    const isAutobook = autobookAvailabilityData?.docData.schedule ?? false;
    const logoUrl = await getClientLogoByClientId(
      autobookAvailabilityData.docData.client
    );
    actions.setClientLogo(logoUrl);

    // handle autobook link
    if (isAutobook) {
      await handleAutobookLink(
        actions,
        autobookAvailabilityData,
        linkType,
        t,
        requestSessionStartedAt
      );
    }

    // handle availability link
    if (!isAutobook) {
      await handleAvailabilityLink(
        actions,
        autobookAvailabilityData,
        linkType,
        logoUrl,
        t,
        requestSessionStartedAt
      );
    }
  }
  actions.setTrackingId(window.crypto.randomUUID());
};
